import React, { useRef } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { sanitize } from "dompurify";
import * as yup from "yup";

import { alertActions } from "../../features/alert/alertSlice";
import { selectSetting } from "../../features/global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { registerUser, verifyEmail } from "../../lib/api";
import { PHONE_REGEXP } from "../../lib/globals";
import { displayFormAlert } from "../../lib/utils";
import { IRegisterOrgUser } from "../../types/forms";

const RegisterOrgUser = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const recaptcha = useRef<ReCAPTCHA>(null);
    const pageText = useAppSelector(state =>
        selectSetting(state, "majorybl_orguser_registration_content"),
    );

    const registerSchema = yup.object().shape({
        firstName: yup.string().required("First Name is required"),
        lastName: yup.string().required("Last Name is required"),
        email: yup
            .string()
            .required("Email address is required")
            .email("Email is invalid"),
        confirmEmail: yup
            .string()
            .required("Please confirm your email address")
            .email("Email is invalid")
            .oneOf([yup.ref("email")], "Email does not match"),
        phone: yup
            .string()
            .required("Phone number is required")
            .matches(PHONE_REGEXP, "Phone number is not valid"),
        password: yup
            .string()
            .required("Please choose a password")
            .min(8, "Password length should be at least 8 characters"),
        confirmPassword: yup
            .string()
            .required("Please confirm your password")
            .min(8, "Password length should be at least 8 characters")
            .oneOf([yup.ref("password")], "Passwords do not match"),
        recaptcha: yup.string().nullable(),
        orgName: yup
            .string()
            .required("Please provide the name of your organization"),
        orgTitle: yup.string(),
        addInfo: yup.string(),
    });

    const verifyCaptcha = (): boolean => {
        const recaptchaValue = recaptcha.current?.getValue();
        if (!recaptchaValue) {
            setError("recaptcha", {
                type: "custom",
                message: "Please verify the reCAPTCHA!",
            });
            return false;
        } else {
            clearErrors("recaptcha");
            return true;
        }
    };

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        setValue,
        formState: { errors },
    } = useForm({
        mode: "onChange",
        resolver: yupResolver(registerSchema),
    });

    const onSubmit: SubmitHandler<IRegisterOrgUser> = async data => {
        try {
            await verifyEmail(data.email);
        } catch (e) {
            setError("email", {
                type: "custom",
                message: "The specified email is already registered",
            });
            return false;
        }

        if (verifyCaptcha()) {
            await registerUser(data, "orguser")
                .then(() => {
                    dispatch(
                        alertActions.success({
                            title: "Registration Successful",
                            text: "Thank you for registering. Once your account has been assigned to your organization, you will have access to all your organization's teams within the Coach's Clubhouse.",
                        }),
                    );
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem registering your account.`,
                            text: "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                });
        }
    };

    const onError: SubmitErrorHandler<IRegisterOrgUser> = (): void => {
        verifyCaptcha();
    };

    const onChangeRecaptcha = async (token: string | null): Promise<void> => {
        const recaptchaValue = recaptcha.current?.getValue();
        setValue("recaptcha", recaptchaValue);
        verifyCaptcha();
        // TODO: Verify response by sending token to back-end API
    };

    return (
        <Form
            onSubmit={handleSubmit(onSubmit, onError)}
            id="registerForm"
            data-testid="registerOrgUserForm"
        >
            <Row>
                <Col>
                    <div
                        className="no-bottom-pad"
                        dangerouslySetInnerHTML={{
                            __html: sanitize(pageText?.setting),
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <h2>Organizational User Registration</h2>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Label>Organization Name *</Form.Label>
                    <Form.Group controlId="orgName">
                        <Form.Control
                            type="text"
                            {...register("orgName")}
                            aria-invalid={errors.orgName ? "true" : "false"}
                        />
                        {errors.orgName &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.orgName.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="orgTitle">
                        <Form.Label>Organizational Title</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("orgTitle")}
                            aria-invalid={errors.orgTitle ? "true" : "false"}
                        />
                        {errors.orgTitle &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.orgTitle.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Form.Label>Name *</Form.Label>
                <Form.Group as={Col} controlId="firstName">
                    <Form.Control
                        type="text"
                        {...register("firstName", {
                            required: "First Name is required",
                        })}
                        aria-invalid={errors.firstName ? "true" : "false"}
                    />
                    <Form.Label>First</Form.Label>
                    {errors.firstName &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.firstName.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="lastName">
                    <Form.Control
                        type="text"
                        {...register("lastName", {
                            required: "Last Name is required",
                        })}
                        aria-invalid={errors.lastName ? "true" : "false"}
                    />
                    <Form.Label>Last</Form.Label>
                    {errors.lastName &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.lastName.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Form.Label>Email *</Form.Label>
                <Form.Group as={Col} controlId="email">
                    <Form.Control
                        type="email"
                        {...register("email", {
                            required: "Email address  is required",
                        })}
                        aria-invalid={errors.email ? "true" : "false"}
                    />
                    <Form.Label>Enter Email</Form.Label>
                    {errors.email &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.email.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="confirmEmail">
                    <Form.Control
                        type="email"
                        {...register("confirmEmail", {
                            required: "Please confirm your email address",
                        })}
                        aria-invalid={errors.confirmEmail ? "true" : "false"}
                    />
                    <Form.Label>Confirm Email</Form.Label>
                    {errors.confirmEmail &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.confirmEmail.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="phone">
                        <Form.Label>Phone *</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("phone")}
                            aria-invalid={errors.phone ? "true" : "false"}
                        />
                        {errors.phone &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.phone.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Form.Label>Password *</Form.Label>
                <Form.Group as={Col} controlId="password">
                    <Form.Control
                        type="password"
                        {...register("password")}
                        aria-invalid={errors.password ? "true" : "false"}
                    />
                    <Form.Label>Enter Password</Form.Label>
                    {errors.password &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.password.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="confirmPassword">
                    <Form.Control
                        type="password"
                        {...register("confirmPassword")}
                        aria-invalid={errors.confirmPassword ? "true" : "false"}
                    />
                    <Form.Label>Confirm Password</Form.Label>
                    {errors.confirmPassword &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.confirmPassword.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Form.Label>
                    Additional Info (ex: Which teams belong in your
                    Organization)
                </Form.Label>
                <Form.Group as={Col} controlId="addInfo">
                    <Form.Control
                        as="textarea"
                        rows={10}
                        cols={50}
                        {...register("addInfo")}
                    />
                </Form.Group>
            </Row>
            <Row>
                <Col xs={3}>
                    <Form.Label>CAPTCHA</Form.Label>
                    <ReCAPTCHA
                        ref={recaptcha}
                        sitekey={process.env.RECAPTCHA_SITE_KEY as string}
                        onChange={onChangeRecaptcha}
                    />
                    {errors.recaptcha &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.recaptcha.message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Col>
                    <Button variant="primary" type="submit">
                        Submit
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default RegisterOrgUser;
